/**
 * Wiederverwendbarer Range-Filter für alle Analytics-Views.
 *
 * Unterstützt:
 *  - Presets: Heute, Gestern, Letzte 7 Tage, Letzte 30 Tage, Dieser Monat,
 *    Letzter Monat
 *  - Benutzerdefiniert: Von-Bis-Kalender (react-day-picker via shadcn)
 *
 * Als Backend-Range-Key werden übergeben:
 *  - `"today" | "yesterday" | "7d" | "30d"` für die Presets
 *  - `"custom:YYYY-MM-DD:YYYY-MM-DD"` für alle Kalender-Auswahlen (auch für
 *    "Dieser Monat" und "Letzter Monat", die intern in ein Custom-Range
 *    konvertiert werden)
 *
 * Timezone-Awareness: die From/To-Grenzen sind reine Kalender-Tage
 * (Europe/Berlin) — das Backend interpretiert sie via `_parse_custom_range`
 * konsistent (Start = 00:00 Berlin am `from`, End = 00:00 Berlin am Tag
 * NACH `to`, exklusiv).
 */
import { useMemo, useState } from "react";
import type * as React from "react";
import { Calendar as CalendarBase } from "@/components/ui/calendar";
import { Popover, PopoverContent as PopoverContentBase, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { de } from "date-fns/locale";

// Die shadcn-Komponenten sind `.jsx` und deshalb nicht sauber getypt.
// Für die Nutzung hier reichen einfache Type-Casts (Runtime unverändert).
type AnyCmp = (props: Record<string, unknown>) => React.ReactElement;
const Calendar = CalendarBase as unknown as AnyCmp;
const PopoverContent = PopoverContentBase as unknown as AnyCmp;

/** Label für die Anzeige — reiner Preset-Key oder eine berechnete Custom-Range. */
function labelFor(rangeKey: string): string {
    if (rangeKey === "today") return "Heute";
    if (rangeKey === "yesterday") return "Gestern";
    if (rangeKey === "7d") return "Letzte 7 Tage";
    if (rangeKey === "30d") return "Letzte 30 Tage";
    if (rangeKey.startsWith("custom:")) {
        const [, f, t] = rangeKey.split(":");
        if (f && t) {
            const fmt = (iso: string) => {
                const [y, m, d] = iso.split("-");
                return `${d}.${m}.${y.slice(2)}`;
            };
            return f === t ? fmt(f) : `${fmt(f)} – ${fmt(t)}`;
        }
    }
    return "Zeitraum";
}

/** YYYY-MM-DD im lokalen Kalendersinne (keine Zeitzone-Skew). */
function toISODateLocal(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function todayLocal(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function firstOfMonth(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

function lastOfMonth(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

type Props = {
    value: string;
    onChange: (next: string) => void;
    /** Optionale zusätzliche Presets, standardmäßig sind alle aktiv. */
    presets?: Array<"today" | "yesterday" | "7d" | "30d" | "this_month" | "last_month" | "custom">;
    testIdPrefix?: string;
};

export default function AnalyticsRangeFilter({
    value,
    onChange,
    presets = ["today", "yesterday", "7d", "30d", "this_month", "last_month", "custom"],
    testIdPrefix = "range-filter",
}: Props) {
    const [open, setOpen] = useState(false);
    const [customMode, setCustomMode] = useState(false);
    const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
    const [toDate, setToDate] = useState<Date | undefined>(undefined);

    const currentLabel = useMemo(() => labelFor(value), [value]);

    const applyPreset = (key: string) => {
        onChange(key);
        setOpen(false);
        setCustomMode(false);
    };

    const applyMonthRange = (which: "this_month" | "last_month") => {
        const now = todayLocal();
        const ref = which === "this_month" ? now : new Date(now.getFullYear(), now.getMonth() - 1, 15);
        const from = firstOfMonth(ref);
        const to = which === "this_month" ? now : lastOfMonth(ref);
        onChange(`custom:${toISODateLocal(from)}:${toISODateLocal(to)}`);
        setOpen(false);
        setCustomMode(false);
    };

    const applyCustom = () => {
        if (!fromDate || !toDate) return;
        const a = fromDate <= toDate ? fromDate : toDate;
        const b = fromDate <= toDate ? toDate : fromDate;
        onChange(`custom:${toISODateLocal(a)}:${toISODateLocal(b)}`);
        setOpen(false);
        setCustomMode(false);
    };

    return (
        <div className="relative inline-block" data-testid={testIdPrefix}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-white text-sm hover:bg-hf-surface transition"
                        style={{ border: "1px solid hsl(var(--hf-line))", minWidth: "180px" }}
                        data-testid={`${testIdPrefix}-trigger`}
                    >
                        <CalendarIcon size={14} />
                        <span className="flex-1 text-left">{currentLabel}</span>
                        <ChevronDown size={14} />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    className="p-0 w-auto"
                    style={{ background: "white", border: "1px solid hsl(var(--hf-line))" }}
                    data-testid={`${testIdPrefix}-popover`}
                >
                    <div className="flex">
                        {/* Preset-Liste links */}
                        <div className="flex flex-col p-2 border-r" style={{ borderColor: "hsl(var(--hf-line))", minWidth: "180px" }}>
                            {presets.includes("today") && (
                                <PresetButton active={value === "today"} onClick={() => applyPreset("today")} testId={`${testIdPrefix}-p-today`}>Heute</PresetButton>
                            )}
                            {presets.includes("yesterday") && (
                                <PresetButton active={value === "yesterday"} onClick={() => applyPreset("yesterday")} testId={`${testIdPrefix}-p-yesterday`}>Gestern</PresetButton>
                            )}
                            {presets.includes("7d") && (
                                <PresetButton active={value === "7d"} onClick={() => applyPreset("7d")} testId={`${testIdPrefix}-p-7d`}>Letzte 7 Tage</PresetButton>
                            )}
                            {presets.includes("30d") && (
                                <PresetButton active={value === "30d"} onClick={() => applyPreset("30d")} testId={`${testIdPrefix}-p-30d`}>Letzte 30 Tage</PresetButton>
                            )}
                            {presets.includes("this_month") && (
                                <PresetButton active={false} onClick={() => applyMonthRange("this_month")} testId={`${testIdPrefix}-p-this-month`}>Dieser Monat</PresetButton>
                            )}
                            {presets.includes("last_month") && (
                                <PresetButton active={false} onClick={() => applyMonthRange("last_month")} testId={`${testIdPrefix}-p-last-month`}>Letzter Monat</PresetButton>
                            )}
                            {presets.includes("custom") && (
                                <PresetButton
                                    active={customMode || value.startsWith("custom:")}
                                    onClick={() => setCustomMode((v) => !v)}
                                    testId={`${testIdPrefix}-p-custom`}
                                >
                                    Benutzerdefiniert…
                                </PresetButton>
                            )}
                        </div>
                        {customMode && (
                            <div className="p-3 flex flex-col gap-3" data-testid={`${testIdPrefix}-custom-panel`}>
                                <div className="flex gap-6">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted mb-1">Von</div>
                                        <Calendar
                                            mode="single"
                                            selected={fromDate}
                                            onSelect={setFromDate}
                                            weekStartsOn={1}
                                            locale={de}
                                            data-testid={`${testIdPrefix}-cal-from`}
                                        />
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted mb-1">Bis</div>
                                        <Calendar
                                            mode="single"
                                            selected={toDate}
                                            onSelect={setToDate}
                                            weekStartsOn={1}
                                            locale={de}
                                            data-testid={`${testIdPrefix}-cal-to`}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 border-t pt-2" style={{ borderColor: "hsl(var(--hf-line))" }}>
                                    <button
                                        type="button"
                                        onClick={() => { setCustomMode(false); setFromDate(undefined); setToDate(undefined); }}
                                        className="px-3 py-1.5 text-xs rounded-sm hover:bg-hf-surface"
                                        style={{ border: "1px solid hsl(var(--hf-line))" }}
                                        data-testid={`${testIdPrefix}-custom-cancel`}
                                    >
                                        Abbrechen
                                    </button>
                                    <button
                                        type="button"
                                        onClick={applyCustom}
                                        disabled={!fromDate || !toDate}
                                        className="px-3 py-1.5 text-xs rounded-sm text-white disabled:opacity-50"
                                        style={{ background: "hsl(var(--hf-ink))" }}
                                        data-testid={`${testIdPrefix}-custom-apply`}
                                    >
                                        Übernehmen
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}

function PresetButton({
    active, onClick, children, testId,
}: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
    testId: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left px-3 py-1.5 rounded-sm text-sm transition ${active ? "text-white" : "text-hf-ink hover:bg-hf-surface"}`}
            style={active ? { background: "hsl(var(--hf-ink))" } : undefined}
            data-testid={testId}
        >
            {children}
        </button>
    );
}
